import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const inr = (n) => '₹' + Math.round(Math.abs(n || 0)).toLocaleString('en-IN');
const VERDICT = {
  as_due: { label: 'As per due', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  extra: { label: 'Extra', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  short: { label: 'Short', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  none: { label: '—', cls: 'bg-zinc-800 text-zinc-500 border-zinc-700' },
};

function Verdict({ verdict, diff, testId }) {
  const v = VERDICT[verdict] || VERDICT.none;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${v.cls}`}>
      {v.label}{verdict === 'extra' && ` +${inr(diff)}`}{verdict === 'short' && ` −${inr(diff)}`}
    </span>
  );
}

function ProductRows({ month }) {
  if (!month.products.length) return <div className="text-zinc-600 text-[11px] px-3 py-2">No products billed this month.</div>;
  return (
    <table className="w-full text-[11px]">
      <tbody>
        {month.products.map(p => (
          <tr key={p.name} className="border-t border-zinc-800/40" data-testid={`cf-product-${month.year}-${month.month}-${p.name}`}>
            <td className="px-3 py-1 text-zinc-300">{p.name}</td>
            <td className="px-3 py-1 text-right font-mono text-amber-300">{p.qty.toLocaleString('en-IN')} {p.unit}</td>
            <td className="px-3 py-1 text-right font-mono text-zinc-400">{inr(p.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const MonthlyCashflowTable = ({ clientId, compact = false, side = '' }) => {
  const [fy, setFy] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const base = clientId ? `${API}/clients/${clientId}/monthly-cashflow` : `${API}/clients/monthly-cashflow`;
  const isCreditor = (data?.side || side) === 'creditor';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(base, { params: { ...(fy ? { fy } : {}), ...(side ? { side } : {}) }, withCredentials: true })
      .then(r => { if (alive) { setData(r.data); if (!fy) setFy(r.data.fy); } })
      .catch(() => toast.error('Could not load monthly cash flow'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [base, fy, side]);

  const download = async () => {
    try {
      const res = await axios.get(base, { params: { fy: fy || data.fy, format: 'xlsx', ...(side ? { side } : {}) }, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${(data.client.name || 'customers').replace(/[^\w]+/g, '_')}_cashflow_FY${fy || data.fy}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 200);
    } catch (e) { toast.error('Excel download failed'); }
  };

  if (loading && !data) return <div className="flex items-center justify-center h-32"><RefreshCw size={18} className="animate-spin text-zinc-500" /></div>;
  if (!data) return null;
  const t = data.totals;

  return (
    <div data-testid="monthly-cashflow">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-2 text-xs">
          <select value={fy || data.fy} onChange={e => setFy(parseInt(e.target.value))} data-testid="cf-fy-select"
            className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-xs">
            {data.available_fys.map(y => <option key={y} value={y}>FY {y}-{String(y + 1).slice(2)}</option>)}
          </select>
          <span className="text-zinc-500">Opening <span className="font-mono text-zinc-300">{inr(data.opening_balance)}{data.opening_balance < 0 ? ' Cr' : ''}</span></span>
          <span className="text-zinc-500">· Closing <span className={`font-mono ${data.closing_balance > 0 ? 'text-red-300' : 'text-emerald-300'}`}>{inr(data.closing_balance)}{data.closing_balance < 0 ? ' Cr' : ''}</span></span>
          {data.client.credit_period_days && <span className="text-zinc-600">· credit {data.client.credit_period_days} d</span>}
        </div>
        <button onClick={download} data-testid="cf-download-xlsx" className="flex items-center gap-1 text-[11px] text-zinc-300 hover:text-amber-300 border border-zinc-700 rounded-lg px-2 py-1">
          <Download size={12} /> Excel
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-950 sticky top-0">
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-wide">
              <th className="text-left px-3 py-2">Month</th>
              <th className="text-right px-3 py-2">{isCreditor ? 'Purchases' : 'Sales'}</th>
              {!compact && <th className="text-right px-3 py-2">Bills</th>}
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Fell due</th>
              <th className="text-right px-3 py-2">{isCreditor ? 'Paid' : 'Received'}</th>
              <th className="text-left px-3 py-2">Paid vs due</th>
              <th className="text-right px-3 py-2">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-zinc-800/60 text-zinc-400 bg-zinc-900/30" data-testid="cf-opening-row">
              <td className="px-3 py-2 whitespace-nowrap pl-7">Opening balance (B/F)</td>
              <td className="px-3 py-2" colSpan={compact ? 5 : 6} />
              <td className={`px-3 py-2 text-right font-mono ${data.opening_balance > 0 ? 'text-red-300/90' : 'text-emerald-300/90'}`}>{inr(data.opening_balance)}{data.opening_balance < 0 ? ' Cr' : ''}</td>
            </tr>
            {data.months.map(m => {
              const key = `${m.year}-${m.month}`;
              const isOpen = !!open[key];
              const dim = m.future || (!m.sales && !m.received && !m.due);
              return (
                <React.Fragment key={key}>
                  <tr data-testid={`cf-row-${key}`} onClick={() => setOpen(o => ({ ...o, [key]: !o[key] }))}
                    className={`border-b border-zinc-800/60 cursor-pointer hover:bg-zinc-900/60 ${dim ? 'text-zinc-600' : 'text-zinc-200'}`}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">{isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}{m.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {m.sales ? inr(m.sales) : '—'}
                      {m.refunds > 0 && <div className="text-[9px] text-rose-300/80 font-sans" data-testid={`cf-refund-${key}`}>incl. {isCreditor ? 'vendor refunded us' : 'payment made to debtor'} {inr(m.refunds)}</div>}
                    </td>
                    {!compact && <td className="px-3 py-2 text-right font-mono">{m.bills || '—'}</td>}
                    <td className="px-3 py-2 text-right font-mono text-amber-300/90">{m.qty ? `${m.qty.toLocaleString('en-IN')} kg` : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{m.due ? inr(m.due) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-300/90">
                      {m.received ? inr(m.received) : '—'}
                      {m.credit_notes > 0 && <div className="text-[9px] text-zinc-500 font-sans">incl. {isCreditor ? 'DN' : 'CN'} {inr(m.credit_notes)}</div>}
                    </td>
                    <td className="px-3 py-2"><Verdict verdict={m.verdict} diff={m.diff} testId={`cf-verdict-${key}`} /></td>
                    <td className={`px-3 py-2 text-right font-mono ${m.closing > 0 ? 'text-red-300/90' : 'text-emerald-300/90'}`}>{inr(m.closing)}{m.closing < 0 ? ' Cr' : ''}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-zinc-900/40" data-testid={`cf-products-${key}`}>
                      <td colSpan={compact ? 7 : 8} className="px-4 py-1">
                        <ProductRows month={m} />
                        {m.refunds > 0 && <div className="text-[10px] text-rose-300/80 px-3 pb-1">Payment made to debtor (refund / loan) this month: {inr(m.refunds)} — counted in {isCreditor ? 'Purchases' : 'Sales'} because they owe it back</div>}
                        {m.credit_notes > 0 && <div className="text-[10px] text-zinc-500 px-3 pb-1">{isCreditor ? 'Debit notes' : 'Credit notes / returns'} this month: {inr(m.credit_notes)} — counted in {isCreditor ? 'Paid' : 'Received'}</div>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-zinc-700 bg-zinc-900/60 text-zinc-100 font-semibold" data-testid="cf-totals">
              <td className="px-3 py-2">Total {data.fy_label}</td>
              <td className="px-3 py-2 text-right font-mono">
                {inr(t.sales)}
                {t.refunds > 0 && <div className="text-[9px] text-rose-300/80 font-sans font-normal">incl. {isCreditor ? 'vendor refunded us' : 'payment made to debtor'} {inr(t.refunds)}</div>}
              </td>
              {!compact && <td className="px-3 py-2 text-right font-mono">{t.bills}</td>}
              <td className="px-3 py-2 text-right font-mono text-amber-300">{t.qty ? `${t.qty.toLocaleString('en-IN')} kg` : '—'}</td>
              <td className="px-3 py-2 text-right font-mono">{inr(t.due)}</td>
              <td className="px-3 py-2 text-right font-mono text-emerald-300">
                {inr(t.received)}
                {t.credit_notes > 0 && <div className="text-[9px] text-zinc-500 font-sans font-normal">incl. {isCreditor ? 'DN' : 'CN'} {inr(t.credit_notes)}</div>}
              </td>
              <td className="px-3 py-2"><Verdict verdict={t.verdict} diff={t.diff} testId="cf-verdict-total" /></td>
              <td className={`px-3 py-2 text-right font-mono ${data.closing_balance > 0 ? 'text-red-300' : 'text-emerald-300'}`}>{inr(data.closing_balance)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="px-3 py-2 text-[10px] text-zinc-600">
        Fell due = bills whose credit period ended that month · {isCreditor ? 'Purchases = bills + money the vendor refunded us' : 'Sales = invoices + payments made to the debtor (refund / loan — they owe it back)'} · {isCreditor ? 'Paid = payments + debit notes dated that month' : 'Received = cash receipts + credit notes dated that month'} · As per due = within 2 % (min ₹100) · tap a month for {isCreditor ? 'material' : 'product'}-wise quantity.
      </div>
    </div>
  );
};
